<template>
  <div class="flex-1 flex flex-col overflow-hidden">
    <div class="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 flex-shrink-0">
      <span class="text-xs font-semibold text-gray-600">{{ $t("ai.widget.previousConversations") }}</span>
      <button
        @click="$emit('close')"
        class="w-6 h-6 rounded flex items-center justify-center bg-gray-100 border border-gray-200 text-gray-500 cursor-pointer hover:bg-gray-200 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center gap-1.5 py-8">
      <span class="dot"></span><span class="dot"></span><span class="dot"></span>
    </div>

    <!-- Empty -->
    <div v-else-if="!conversations.length" class="flex-1 flex flex-col items-center justify-center gap-2.5 text-gray-400 text-sm py-10">
      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="1.5">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      <p class="m-0">{{ $t("ai.widget.noConversations") }}</p>
    </div>

    <!-- List -->
    <div v-else class="flex-1 overflow-y-auto p-2 history-scroll">
      <div
        v-for="conv in conversations"
        :key="conv.uuid"
        :class="conv.uuid === conversationId ? 'bg-indigo-50 border-indigo-200' : 'border-transparent hover:bg-gray-50 hover:border-gray-100'"
        class="flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer border mb-1 transition-colors"
        @click="$emit('load-conversation', conv)"
      >
        <div class="flex-1 min-w-0">
          <p class="m-0 mb-1 text-sm text-gray-700 font-medium truncate">
            {{ conv.title || $t("ai.widget.tabChat") }}
          </p>
          <span class="flex gap-2 items-center">
            <span class="text-xs text-gray-400">{{ formatDate(conv.updated_at || conv.created_at) }}</span>
            <span v-if="conv.messages_count" class="text-xs text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded-full border border-indigo-100">
              {{ conv.messages_count }} msg
            </span>
          </span>
        </div>
        <button
          @click.stop="$emit('delete-conversation', conv.uuid)"
          class="w-7 h-7 rounded flex-shrink-0 flex items-center justify-center text-gray-400 border border-transparent cursor-pointer transition-colors hover:bg-red-50 hover:border-red-200 hover:text-red-500"
          :title="$t('delete')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AiChatHistory",

  props: {
    conversations:  { type: Array,  default: () => [] },
    loading:        { type: Boolean, default: false },
    conversationId: { type: String,  default: null },
  },

  emits: ["load-conversation", "delete-conversation", "close"],

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      const now = new Date();
      const mins = Math.floor((now - d) / 60000);
      if (mins < 60) return this.$t("ai.widget.minutesAgo", { n: mins });
      const hours = Math.floor(mins / 60);
      if (hours < 24) return this.$t("ai.widget.hoursAgo", { n: hours });
      return d.toLocaleDateString(this.$i18n.locale, { month: "short", day: "numeric" });
    },
  },
};
</script>

<style scoped>
.history-scroll::-webkit-scrollbar { width: 4px; }
.history-scroll::-webkit-scrollbar-track { background: transparent; }
.history-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: #94a3b8;
  border-radius: 50%;
  animation: ai-bounce 1.4s infinite both;
}
.dot:nth-child(1) { animation-delay: -0.32s; }
.dot:nth-child(2) { animation-delay: -0.16s; }
@keyframes ai-bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
  40%            { transform: scale(1); opacity: 1; }
}
</style>
